<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{'search-popup-overlay': true, 'is-visible': isVisible }"
    @click="cancel()">
    <div
      class="search-popup"
      @click.stop="popupClicked">
      <a
        href="#"
        class="search-popup-close"
        @click.prevent="cancel()">
        &times;
      </a>

      <header class="search-popup-header">
        <h2>{{ $t('searchPopup.title') }}</h2>

        <field>
          <label
            slot="label">
            {{ $t('searchPopup.insertNamePhoneOrEmail') }}
          </label>

          <input
            slot="field"
            v-model="searchPhrase"
            @input="getSearchResults" />
        </field>

        <small
          v-if="loading && pagination.currentPage === 1"
          class="loading-state">
          {{ $t('searchPopup.loadingResults') }}
        </small>

        <small
          v-if="!loading && searchPhrase !== '' && !results.length"
          class="empty-state">
          {{ $t('searchPopup.noResultsForGivenPhrase') }}
        </small>
      </header>

      <div
        :class="{ 'results': true, 'has-overflow': resultsHasOverflow }"
        ref="results-list">
        <search-popup-reservation
          v-for="(result, index) of results"
          :itemData="result"
          :itemIndex="index"
          :key="'search-popup-reservation-result-' + index" />
        <btn
          v-if="results.length && pagination.currentPage < pagination.maxPage"
          class="load-more-results"
          @click.native="loadAdditionalResults"
          :disabled="loading">
          <template v-if="loading">{{ $t('common.ui.loading') }}</template>
          <template v-else>{{ $t('common.ui.loadMoreResults') }}</template>
        </btn>
      </div>

      <btn
        type="danger full-width"
        @click.native="cancel()">
        {{ $t('common.ui.close') }}
      </btn>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import ErrorUtils from './../helpers/ErrorUtils.js';
import PopupZindex from './../mixins/PopupZindex';
import SearchPopupReservation from '../ui/SearchPopupReservation';

export default {
  name: 'search-popup',
  mixins: [
    PopupZindex
  ],
  components: {
    'search-popup-reservation': SearchPopupReservation
  },
  data () {
    return {
      isVisible: false,
      searchPhrase: '',
      loading: false,
      results: [],
      resultsHasOverflow: false,
      pagination: {
        currentPage: 1,
        maxPage: 1
      }
    };
  },
  mounted () {
    this.$bus.$on('search-popup-display', this.show);
    this.$bus.$on('search-popup-hide', this.hide);
    this.$bus.$on('search-popup-refresh', this.search);
  },
  methods: {
    show (searchPhrase) {
      this.isVisible = true;
      this.searchPhrase = searchPhrase;
      this.search();
      this.increasePopupZindex();
    },
    cancel () {
      this.searchPhrase = '';
      this.results = [];
      this.resultsHasOverflow = false;
      this.$refs['results-list'].scrollTop = 0;
      this.hide();
    },
    hide () {
      this.isVisible = false;
      this.removePopupZindex();
    },
    getSearchResults () {
      if (this.searchPhrase !== '') {
        this.loading = true;
        this.debouncedSearch();
      } else {
        this.loading = false;
        this.results = [];
        this.resultsHasOverflow = false;
      }
    },
    debouncedSearch: debounce(function () {
      this.search();
    }, 500),
    search () {
      this.loading = true;

      if (this.searchPhrase === '') {
        this.loading = false;
        this.results = [];
        this.resultsHasOverflow = false;
        return false;
      }

      this.$http.post('/panel/general/search-reservations-results', {
        search: encodeURIComponent(this.searchPhrase)
      }).then(response => {
        this.pagination.currentPage = 1;
        this.pagination.maxPage = parseInt(response.data.meta.last_page, 10);
        this.loading = false;
        this.results = response.data.data;

        setTimeout(() => {
          if (this.$refs['results-list'].getBoundingClientRect().height >= 300) {
            this.resultsHasOverflow = true;
          }
        }, 50);
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/general/search-reservations-results', this.$router, this.$bus, this.search.bind(this));
        this.loading = false;
      });
    },
    loadAdditionalResults () {
      if (this.loading) {
        return;
      }

      this.pagination.currentPage++;
      this.loading = true;

      this.$http.post('/panel/general/search-reservations-results', {
        search: encodeURIComponent(this.searchPhrase),
        page: this.pagination.currentPage
      }).then(response => {
        this.loading = false;
        this.results = this.results.concat(response.data.data);
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/general/search-reservations-results', this.$router, this.$bus, this.search.bind(this));
        this.loading = false;
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('search-popup-display', this.show);
    this.$bus.$off('search-popup-hide', this.hide);
    this.$bus.$off('search-popup-refresh', this.search);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.search-popup {
  background: $color-light;
  border-radius: $border-radius-small;
  box-shadow: 0 5px 5px rgba(0, 0, 0, .25);
  left: 50%;
  max-height: 90vh;
  padding: 2rem;
  position: fixed;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  transition: transform .25s ease-out;
  width: 84rem;
  will-change: transform;
  z-index: 10001;

  & > .button {
    display: none;
  }

  &-overlay {
    background: rgba(0, 0, 0, .25);
    height: 100%;
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: fixed;
    top: 0;
    transition: opacity .25s ease-out;
    width: 100vw;
    will-change: opacity;
    z-index: 10000;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;

      .confirm {
        transform: translateX(-50%) translateY(-50%);
      }
    }
  }

  &-close {
    background: $color-danger;
    border-radius: 50%;
    color: $color-light;
    font-size: 3.6rem;
    font-weight: bold;
    height: 3.6rem;
    line-height: 3.5rem;
    position: absolute;
    right: -1.8rem;
    text-align: center;
    top: -1.8rem;
    width: 3.6rem;
  }

  h2 {
    font-weight: 400;
    margin: 0 0 2rem 0;
  }

  p {
    color: $color-text;
  }

  .loading-state,
  .empty-state {
    color: $color-text-light;
    display: block;
    font-size: 1.4rem;
    margin: 2rem 0;
    text-align: center;
    width: 100%;
  }

  .field {
    label {
      text-align: left;
      padding-right: .5rem;
      width: 50%;
    }

    input {
      width: 50%;
    }
  }

  .results {
    height: 480px;
    max-height: calc(90vh - 160px);

    &.has-overflow {
      overflow: auto;
      -webkit-overflow-scrolling: touch;
    }
  }

  .load-more-results {
    margin: 2rem auto;
  }
}

@media (max-width: 900px) {
  .search-popup {
    border-radius: 0;
    height: 100%;
    left: 0;
    max-height: none;
    padding: 1rem;
    top: 0;
    transform: none;
    width: 100vw;

    .search-popup-close {
      display: none;
    }

    .field {
      label {
        font-size: 1.4rem;
      }
    }

    .search-popup-header {
      h2 {
        margin-bottom: 0;
      }
    }

    .results {
      height: calc(100% - 125px);
      max-height: 100%;
    }

    & > .button {
      border-radius: 0;
      bottom: 0;
      display: block;
      left: 0;
      position: absolute;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/SearchPopup.vue